import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { DateRange, MatDateRangePicker } from '@angular/material/datepicker';
import { MatSelectionListChange } from '@angular/material/list';
import { DateRange as CustomDateRange } from '@common/data-models/complex-types/date-range';
import { EnpsControlComponent } from '@src/suite/common/controls/enps-control/enps-control';
import { CustomFilterDef } from '@src/suite/common/data-models/navigation/custom-filter-def';
import { selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import moment, { type Moment } from 'moment-timezone';

@Component({
    selector: 'st-date-range-picker',
    templateUrl: './date-range-picker.html',
    styleUrls: ['./date-range-picker.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'st-date-range-picker',
    },
})
export class DateRangePickerComponent extends EnpsControlComponent<CustomDateRange> {
    dateAdapter = inject(DateAdapter<Moment>);

    dateRangePicker = viewChild<MatDateRangePicker<Moment>>('picker');

    // signals
    selectedDateRange = signal<DateRange<Date>>(null);
    periods = signal<Array<{ id: string; label: string }>>([]);
    selectedPeriod = signal<string>(null);
    internalValue = signal<CustomDateRange>(new CustomDateRange());

    // computed
    startDateLabel = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebStartDateLabel))();
    });

    endDateLabel = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebEndDateLabel))();
    });

    cancelButtonLabel = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebCancelButtonLabel))();
    });

    applyButtonLabel = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebApplyButtonLabel))();
    });

    dateRange = computed(() => {
        if (this.internalValue().custom) {
            const id: string = this.internalValue().custom.replace('@date_range_', '').split('_').join('').toUpperCase();
            if (this.globalTablesService.CustomFilterDefsDict[id]) {
                return this.globalTablesService.CustomFilterDefsDict[id].Caption;
            }
        } else if (this.dateLocale()) {
            return this.internalValue().toLocaleString();
        }
        return null;
    });

    private emit: boolean = false;

    range = new FormGroup({
        from: new FormControl<Moment | null>(null),
        to: new FormControl<Moment | null>(null),
    });

    private prev: any = null;

    constructor() {
        super();
        for (let i = 0; i < this.globalTablesService.CustomFilterDef.length; i++) {
            const customFilterDef: CustomFilterDef = this.globalTablesService.CustomFilterDef[i];
            if (customFilterDef.Validation === 'DATE' || customFilterDef.Validation === 'TIME') {
                this.periods.update(periods => {
                    periods.push({
                        id: `@date_range_${customFilterDef.ID}`,
                        label: customFilterDef.Caption,
                    });
                    return periods;
                });
            }
        }
        effect(
            () => {
                const val = this.value();
                if (!this.internalValue().valid() || val !== this.prev) this.internalValue.set(val || new CustomDateRange());
                this.processDateRangeChange(false);
                this.prev = val;
                if (this.dateLocale()) {
                    this.dateAdapter.setLocale(this.dateLocale());
                }
            },
            { allowSignalWrites: true }
        );
    }

    onEnter() {
        const val = this.range.getRawValue();
        const range = new CustomDateRange();
        range.custom = null;
        range.from = val.from ? val.from.startOf('day').toDate() : null;
        range.to = val.to ? val.to.startOf('day').subtract(1, 'second').toDate() : null;
        this.internalValue.set(range);
        this.changed.emit(this.internalValue());
    }

    onSelectionChange(event: MatSelectionListChange) {
        const value = event.source.selectedOptions.selected.map(item => item.value);
        const val = new CustomDateRange();
        val.custom = value[0];
        val.from = null;
        val.to = null;
        this.internalValue.set(val);
        this.processDateRangeChange(true);
    }

    onApply() {
        // Check for single date
        let val = this.range.getRawValue();
        if (!val.to) {
            this.range.setValue({
                from: val.from.startOf('day'),
                to: val.from.add(1, 'day').startOf('day').subtract(1, 'second'),
            });
        }
        val = this.range.getRawValue();
        const range = new CustomDateRange();
        range.custom = null;
        range.from = val.from ? val.from.startOf('day').toDate() : null;
        range.to = val.to ? val.to.startOf('day').add(1, 'day').subtract(1, 'second').toDate() : null;
        this.internalValue.set(range);
        this.selectedPeriod.set(null);
        this.emit = true;
    }

    onClosed() {
        if (this.emit) {
            this.changed.emit(this.internalValue());
            this.emit = false;
        }
    }

    private processDateRangeChange(emit: boolean) {
        if (this.internalValue().custom) {
            const id: string = this.internalValue().custom.replace('@date_range_', '').split('_').join('').toUpperCase();
            let custom: string;
            if (this.globalTablesService.CustomFilterDefsDict[id]) {
                custom = this.globalTablesService.CustomFilterDefsDict[id].Value;
            }
            const val = this.internalValue().get();

            if (custom === 'all' || custom === 'blank') {
                this.range.setValue({
                    from: null,
                    to: null,
                });
            } else if (custom === 'future') {
                this.range.setValue({
                    from: val?.[0] ? moment(val[0]) : null,
                    to: null,
                });
            } else {
                this.range.setValue({
                    from: val?.[0] ? moment(val[0]) : null,
                    to: val?.[1] ? moment(val[1]) : null,
                });
            }

            if (this.dateRangePicker) {
                if (emit) this.emit = true;
                this.dateRangePicker().close();
            }
        } else {
            this.range.setValue({
                from: this.internalValue().from ? moment(this.internalValue().from) : null,
                to: this.internalValue().to ? moment(this.internalValue().to) : null,
            });

            this.selectedPeriod.set(null);
        }
    }
}
